import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Users, UserMinus, Plus, Trash2, Search, Pencil, Save, X, Upload, Download } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 20 columns per the DBK Staff Details master sheet (in display order)
const COLUMNS = [
  { key: 'srNo',          label: 'SR.NO',         w: 60,  align: 'center' },
  { key: 'externalEmpId', label: 'EMPLOYEE ID',   w: 100, align: 'left',   editable: true, type: 'text' },
  { key: 'employeeName',  label: 'NAME',          w: 180, align: 'left',   editable: true, type: 'text', sticky: true, required: true },
  { key: 'designation',   label: 'DESIGNATION',   w: 140, align: 'left',   editable: true, type: 'text' },
  { key: 'joiningDate',   label: 'JOINING DATE',  w: 120, align: 'left',   editable: true, type: 'date' },
  { key: 'resignDate',    label: 'RESIGN DATE',   w: 120, align: 'left',   editable: true, type: 'date' },
  { key: 'salary',        label: 'SALARY',        w: 100, align: 'right',  editable: true, type: 'number' },
  { key: 'mobile',        label: 'MO.NO',         w: 110, align: 'left',   editable: true, type: 'text' },
  { key: 'email',         label: 'EMAIL ID',      w: 200, align: 'left',   editable: true, type: 'email' },
  { key: 'address',       label: 'ADDRESS',       w: 200, align: 'left',   editable: true, type: 'text' },
  { key: 'birthDate',     label: 'BIRTH DATE',    w: 120, align: 'left',   editable: true, type: 'date' },
  { key: 'aadharCard',    label: 'AADHAR CARD',   w: 130, align: 'left',   editable: true, type: 'text' },
  { key: 'panCard',       label: 'PAN CARD',      w: 110, align: 'left',   editable: true, type: 'text' },
  { key: 'qualification', label: 'QUALIFICATION', w: 130, align: 'left',   editable: true, type: 'text' },
  { key: 'uanNo',         label: 'UAN NO',        w: 120, align: 'left',   editable: true, type: 'text' },
  { key: 'pfNo',          label: 'PF NO',         w: 120, align: 'left',   editable: true, type: 'text' },
  { key: 'esicNo',        label: 'ESIC NO',       w: 120, align: 'left',   editable: true, type: 'text' },
  { key: 'bankName',      label: 'BANK NAME',     w: 140, align: 'left',   editable: true, type: 'text' },
  { key: 'accountNo',     label: 'ACCOUNT NO',    w: 140, align: 'left',   editable: true, type: 'text' },
  { key: 'ifscCode',      label: 'IFSC CODE',     w: 110, align: 'left',   editable: true, type: 'text' },
];

const fmt = (v, type) => {
  if (v === null || v === undefined || v === '') return '—';
  if (type === 'number') return Number(v).toLocaleString('en-IN');
  return String(v);
};

// One reusable table for both Current & Ex sub-tabs
const StaffTable = ({ status }) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});
  const [removeMode, setRemoveMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [showAdd, setShowAdd] = useState(false);
  const [importing, setImporting] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newDraft, setNewDraft] = useState({ employeeName: '', designation: '', externalEmpId: '' });
  const fileInput = useRef(null);

  const token = localStorage.getItem('crm_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/hr/attendance-employees`, {
        params: { status },
        headers,
      });
      setEmployees(res.data.employees || []);
    } catch {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter(e =>
      (e.employeeName || '').toLowerCase().includes(q) ||
      (e.designation || '').toLowerCase().includes(q) ||
      (e.externalEmpId || '').toLowerCase().includes(q) ||
      (e.mobile || '').includes(q) ||
      (e.email || '').toLowerCase().includes(q) ||
      (e.aadharCard || '').includes(q) ||
      (e.panCard || '').toLowerCase().includes(q)
    );
  }, [employees, searchTerm]);

  const startEdit = (emp) => {
    setEditingId(emp.employeeId);
    setDraft({ ...emp });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft({});
  };

  const saveEdit = async (empId) => {
    if (!draft.employeeName?.trim()) { toast.error('Name is required'); return; }
    try {
      // PUT with full body (existing endpoint handles partial updates via the
      // AttendanceEmployee model — extended fields are optional)
      await axios.put(`${API}/hr/attendance-employees/${empId}`, draft, { headers });
      // Optimistically update local state
      setEmployees(prev => prev.map(e => (e.employeeId === empId ? { ...e, ...draft } : e)));
      toast.success('Saved');
      cancelEdit();
    } catch {
      toast.error('Save failed');
    }
  };

  const removeSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Remove ${selected.size} employee(s)?`)) return;
    try {
      for (const empId of selected) {
        await axios.delete(`${API}/hr/attendance-employees/${empId}`, { headers });
      }
      setEmployees(prev => prev.filter(e => !selected.has(e.employeeId)));
      setSelected(new Set());
      setRemoveMode(false);
      toast.success('Removed');
    } catch {
      toast.error('Remove failed');
    }
  };

  const moveToStatus = async (empId, newStatus) => {
    try {
      const emp = employees.find(e => e.employeeId === empId);
      if (!emp) return;
      await axios.put(`${API}/hr/attendance-employees/${empId}`,
        { ...emp, status: newStatus },
        { headers });
      setEmployees(prev => prev.filter(e => e.employeeId !== empId));
      toast.success(newStatus === 'ex' ? 'Moved to Ex-Employees' : 'Restored to Current');
    } catch {
      toast.error('Failed to update status');
    }
  };

  const addEmployee = async () => {
    if (!newDraft.employeeName?.trim()) { toast.error('Name is required'); return; }
    setAdding(true);
    try {
      const res = await axios.post(`${API}/hr/attendance-employees`, {
        employeeName: newDraft.employeeName.trim(),
        designation: newDraft.designation || '',
        status,
      }, { headers });
      // Server returns {success, employee}
      const emp = res.data.employee || res.data;
      // Optionally patch externalEmpId via PUT
      if (newDraft.externalEmpId?.trim()) {
        await axios.put(`${API}/hr/attendance-employees/${emp.employeeId}`,
          { ...emp, externalEmpId: newDraft.externalEmpId.trim() },
          { headers });
      }
      setShowAdd(false);
      setNewDraft({ employeeName: '', designation: '', externalEmpId: '' });
      fetchData();
      toast.success(`${emp.employeeName} added`);
    } catch {
      toast.error('Add failed');
    } finally {
      setAdding(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('status', status);
      const res = await axios.post(`${API}/hr/attendance-employees/import`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      const { inserted = 0, updated = 0, skipped = 0 } = res.data;
      toast.success(`Imported: +${inserted} new · ${updated} updated · ${skipped} skipped`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const StatusBtn = ({ emp }) => (
    <Button
      size="sm"
      variant="outline"
      className={`h-6 px-2 text-[10px] ${status === 'current' ? 'border-amber-300 text-amber-700 hover:bg-amber-50' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'}`}
      onClick={(e) => { e.stopPropagation(); moveToStatus(emp.employeeId, status === 'current' ? 'ex' : 'current'); }}
      data-testid={`status-toggle-${emp.employeeId}`}
      title={status === 'current' ? 'Move to Ex-Employees' : 'Restore to Current'}
    >
      {status === 'current' ? <UserMinus className="w-3 h-3 mr-0.5" /> : <Users className="w-3 h-3 mr-0.5" />}
      {status === 'current' ? 'Move to Ex' : 'Restore'}
    </Button>
  );

  const EditableCell = ({ col, row }) => {
    const v = draft[col.key] ?? '';
    return (
      <input
        type={col.type || 'text'}
        value={v}
        onChange={e => setDraft(prev => ({ ...prev, [col.key]: col.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value }))}
        className="w-full bg-yellow-50 border border-yellow-300 rounded px-1 py-0.5 text-[11px] focus:outline-none focus:border-yellow-500"
        data-testid={`edit-cell-${row.employeeId}-${col.key}`}
      />
    );
  };

  return (
    <div className="space-y-3" data-testid={`staff-table-${status}`}>
      {/* Action bar */}
      <div className="flex flex-wrap items-center justify-between gap-2 bg-stone-50 border border-stone-200 rounded-lg p-2">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-blue-600" />
          <span className="font-bold text-sm">DBK STAFF DETAILS</span>
          <span className="bg-blue-100 text-blue-700 text-[10px] font-bold px-2 py-0.5 rounded-full">
            {filtered.length} / {employees.length} {status === 'ex' ? 'ex-' : ''}employee{employees.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-2 text-stone-400" />
            <Input
              data-testid={`staff-search-${status}`}
              placeholder="Search name, designation, ID, mobile, email, PAN…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 h-8 text-xs w-72"
            />
          </div>
          <input
            ref={fileInput}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={handleImport}
          />
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
            disabled={importing}
            onClick={() => fileInput.current?.click()}
            data-testid={`import-staff-btn-${status}`}
          >
            <Upload className="w-3 h-3 mr-1" />
            {importing ? 'Importing…' : 'Import Excel'}
          </Button>
          {!removeMode && (
            <Button
              size="sm"
              className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700"
              onClick={() => setShowAdd(!showAdd)}
              data-testid={`add-emp-btn-${status}`}
            >
              <Plus className="w-3 h-3 mr-1" /> Add Employee
            </Button>
          )}
          {!removeMode ? (
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs border-red-300 text-red-600 hover:bg-red-50"
              onClick={() => { setRemoveMode(true); setShowAdd(false); }}
              data-testid={`remove-mode-btn-${status}`}
            >
              <Trash2 className="w-3 h-3 mr-1" /> Remove
            </Button>
          ) : (
            <div className="flex gap-1">
              <Button
                size="sm"
                className="h-8 text-xs bg-red-600 hover:bg-red-700"
                disabled={selected.size === 0}
                onClick={removeSelected}
                data-testid={`confirm-remove-btn-${status}`}
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Confirm ({selected.size})
              </Button>
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => { setRemoveMode(false); setSelected(new Set()); }}>Cancel</Button>
            </div>
          )}
        </div>
      </div>

      {/* Add inline form */}
      {showAdd && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[140px]">
            <label className="text-[10px] font-medium text-stone-600 mb-0.5 block">Employee ID</label>
            <Input
              data-testid={`new-emp-id-${status}`}
              placeholder="e.g. SBD-0048"
              value={newDraft.externalEmpId}
              onChange={e => setNewDraft(prev => ({ ...prev, externalEmpId: e.target.value }))}
              className="h-8 text-xs"
            />
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="text-[10px] font-medium text-stone-600 mb-0.5 block">Name *</label>
            <Input
              data-testid={`new-emp-name-${status}`}
              placeholder="e.g. RAHUL SHARMA"
              value={newDraft.employeeName}
              onChange={e => setNewDraft(prev => ({ ...prev, employeeName: e.target.value }))}
              className="h-8 text-xs"
            />
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="text-[10px] font-medium text-stone-600 mb-0.5 block">Designation</label>
            <Input
              data-testid={`new-emp-designation-${status}`}
              placeholder="e.g. Manager"
              value={newDraft.designation}
              onChange={e => setNewDraft(prev => ({ ...prev, designation: e.target.value }))}
              className="h-8 text-xs"
            />
          </div>
          <Button
            size="sm"
            className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700"
            disabled={adding || !newDraft.employeeName.trim()}
            onClick={addEmployee}
            data-testid={`confirm-add-emp-btn-${status}`}
          >
            {adding ? 'Adding…' : 'Add'}
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setShowAdd(false)}>Cancel</Button>
        </div>
      )}

      {/* Yellow banner header (matches Excel screenshot) */}
      <div className="bg-yellow-300 border-2 border-stone-800 px-4 py-2 font-extrabold text-sm tracking-wider text-stone-900">
        DBK STAFF DETAILS
      </div>

      {/* Detailed table */}
      <Card className="rounded-t-none">
        <CardContent className="p-0">
          <div className="overflow-auto" style={{ maxHeight: 620 }}>
            <table className="text-[11px] border-collapse" style={{ minWidth: 2400 }}>
              <thead className="sticky top-0 z-20 bg-stone-200">
                <tr>
                  {removeMode && <th className="px-2 py-2 border border-stone-400 w-10 sticky left-0 bg-stone-200 z-30"></th>}
                  {COLUMNS.map((c, i) => (
                    <th
                      key={c.key}
                      className={`px-2 py-2 border border-stone-400 font-bold text-stone-900 whitespace-nowrap ${c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : 'text-left'} ${c.sticky ? 'sticky bg-stone-200 z-30' : ''}`}
                      style={{ width: c.w, minWidth: c.w, ...(c.sticky ? { left: removeMode ? 40 + 60 : 60 } : {}) }}
                    >
                      {c.label}
                    </th>
                  ))}
                  <th className="px-2 py-2 border border-stone-400 font-bold text-stone-900 text-center sticky right-0 bg-stone-200 z-30" style={{ width: 180, minWidth: 180 }}>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={COLUMNS.length + (removeMode ? 2 : 1)} className="py-8 text-center text-stone-400">Loading…</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={COLUMNS.length + (removeMode ? 2 : 1)} className="py-8 text-center text-stone-400">No employees match.</td></tr>
                )}
                {!loading && filtered.map((emp, idx) => {
                  const isEditing = editingId === emp.employeeId;
                  const isSelected = selected.has(emp.employeeId);
                  const rowBg = isSelected ? '#fee2e2' : (isEditing ? '#fefce8' : (idx % 2 === 0 ? '#ffffff' : '#fafaf9'));
                  return (
                    <tr key={emp.employeeId}>
                      {removeMode && (
                        <td className="px-2 py-1 border border-stone-200 text-center sticky left-0 z-10" style={{ backgroundColor: rowBg }}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              setSelected(prev => {
                                const n = new Set(prev);
                                if (n.has(emp.employeeId)) n.delete(emp.employeeId); else n.add(emp.employeeId);
                                return n;
                              });
                            }}
                            data-testid={`remove-checkbox-${emp.employeeId}`}
                            className="w-3.5 h-3.5 accent-red-500"
                          />
                        </td>
                      )}
                      {COLUMNS.map(c => (
                        <td
                          key={c.key}
                          className={`px-2 py-1 border border-stone-200 ${c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : 'text-left'} ${c.key === 'employeeName' ? 'font-bold' : ''} ${c.sticky ? 'sticky z-10' : ''}`}
                          style={{ width: c.w, maxWidth: c.w, backgroundColor: rowBg, ...(c.sticky ? { left: removeMode ? 40 + 60 : 60 } : {}) }}
                          title={String(emp[c.key] ?? '')}
                        >
                          {isEditing && c.editable ? (
                            <EditableCell col={c} row={emp} />
                          ) : (
                            <span className="truncate inline-block max-w-full">{fmt(emp[c.key] ?? (c.key === 'srNo' ? idx + 1 : ''), c.type)}</span>
                          )}
                        </td>
                      ))}
                      <td className="px-2 py-1 border border-stone-200 text-center sticky right-0 z-10" style={{ width: 180, backgroundColor: rowBg }}>
                        {isEditing ? (
                          <div className="flex gap-1 justify-center">
                            <Button size="sm" className="h-6 px-2 text-[10px] bg-blue-600 hover:bg-blue-700" onClick={() => saveEdit(emp.employeeId)} data-testid={`save-edit-${emp.employeeId}`}>
                              <Save className="w-3 h-3 mr-0.5" /> Save
                            </Button>
                            <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={cancelEdit}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex gap-1 justify-center">
                            <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] border-blue-200 text-blue-600 hover:bg-blue-50" onClick={() => startEdit(emp)} data-testid={`edit-emp-btn-${emp.employeeId}`}>
                              <Pencil className="w-3 h-3 mr-0.5" /> Edit
                            </Button>
                            <StatusBtn emp={emp} />
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Helper note */}
      <div className="text-[10px] text-stone-500 px-2">
        Tip: Click <span className="font-semibold">Import Excel</span> to bulk-load employees from a DBK Staff Details sheet.
        Existing employees (matched by Employee ID or Name) are updated in place — no duplicates.
      </div>
    </div>
  );
};

const EmployeeManagementTab = () => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <Users className="w-5 h-5 text-blue-600" />
        Employee Management
      </CardTitle>
    </CardHeader>
    <CardContent>
      <Tabs defaultValue="current">
        <TabsList className="bg-stone-100" data-testid="employee-mgmt-subtabs">
          <TabsTrigger value="current" data-testid="tab-current-employees" className="data-[state=active]:bg-white">
            <Users className="w-3.5 h-3.5 mr-1.5" /> Current Employees
          </TabsTrigger>
          <TabsTrigger value="ex" data-testid="tab-ex-employees" className="data-[state=active]:bg-white">
            <UserMinus className="w-3.5 h-3.5 mr-1.5" /> Ex-Employees
          </TabsTrigger>
        </TabsList>
        <TabsContent value="current" className="mt-4">
          <StaffTable status="current" />
        </TabsContent>
        <TabsContent value="ex" className="mt-4">
          <StaffTable status="ex" />
        </TabsContent>
      </Tabs>
    </CardContent>
  </Card>
);

export default EmployeeManagementTab;
